import {
  EXTERNAL_PROJECT_STATUSES,
  EXTERNAL_PROJECT_TYPES,
  PROJECT_STATUSES,
  PROJECT_TYPES,
  ProjectType
} from '@/api-client/models/project/Contracts'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { Feature } from '@/pinia/feature'
import { RGBA, colorBetween } from '@/utils/rgba'
import { ticketAnalysis } from './ticketAnalysis'

type ProjectAnalysisOptions = {
  internal: boolean
}

const toColorScale = (color) => ({
  type: 'INTERPOLATE',
  cutoffs: [
    {
      value: 0,
      color: colorBetween(RGBA.fromHex('#ffffff'), RGBA.fromHex(color), 0.1).toHex()
    },
    { value: 10, color }
  ]
})

export const PROJECT_STATUSES_MEASUREMENTS = [
  'IN_PROGRESS',
  'PLANNED',
  'NOT_STARTED',
  'COMPLETED'
] as const

const detailHeaders = [
  {
    en: 'Project type',
    sv: 'Projekttyp'
  },
  {
    en: 'Number',
    sv: 'Antal'
  }
]

const detailRowNames: Record<ProjectType, LocaleString> = {
  ADMINISTRATIVE: {
    en: 'Administrative',
    sv: 'Administrativ'
  },
  TENANT_ADAPTATION: {
    en: 'Tenant adaptation',
    sv: 'Hyresgästanpassning'
  },
  MISCELLANEOUS: {
    en: 'Miscellaneous',
    sv: 'Övrigt'
  },
  ENERGY: {
    en: 'Energy',
    sv: 'Energi'
  },
  DEVELOPMENT: {
    en: 'Development',
    sv: 'Utveckling'
  },
  BUSINESS_INVESTIGATION: {
    en: 'Business investigation',
    sv: 'Affärsutredning'
  },
  INVESTMENT: {
    en: 'Investment',
    sv: 'Investering'
  },
  NEW_CONSTRUCTION: {
    en: 'New construction',
    sv: 'Nybyggnation'
  },
  MAINTENANCE: {
    en: 'Maintenance',
    sv: 'Underhåll'
  },
  STRATEGIC_INVESTIGATION: {
    en: 'Strategic investigation',
    sv: 'Strategisk utredning'
  },
  UNKNOWN: {
    en: 'Unknown',
    sv: 'Okänd'
  }
}

const titles = {
  IN_PROGRESS: {
    en: 'Ongoing projects',
    sv: 'Pågående projekt'
  },
  PLANNED: {
    en: 'Planned projects',
    sv: 'Planerade projekt'
  },
  NOT_STARTED: {
    sv: 'Ej påbörjade projekt',
    en: 'Projects not started'
  },
  COMPLETED: {
    en: 'Completed projects',
    sv: 'Avslutade projekt'
  }
}

const colors: Record<string, string> = {
  IN_PROGRESS: '#40A9FF',
  PLANNED: '#9254DE',
  NOT_STARTED: '#F759AB',
  COMPLETED: '#36CFC9'
}

const createProjectAnalysisChild = ({ internal }: ProjectAnalysisOptions) => ({
  name: internal ? 'internal' : 'external',
  enabled: true,
  isRoute: true,
  access: [
    internal ? 'FEAT_PROJECT-ANALYSIS-INTERNAL_READER' : 'FEAT_PROJECT-ANALYSIS-EXTERNAL_READER'
  ],
  title: {
    sv: 'Projekt ' + (internal ? ' (internt)' : ' (externt)'),
    en: 'Projects ' + (internal ? ' (internal)' : ' (external)')
  },
  component: 'DashboardFeature',
  props: {
    showCalendar: false,
    defaultFilters: {
      projectTypes: internal ? PROJECT_TYPES : EXTERNAL_PROJECT_TYPES,
      projectStatuses: internal ? PROJECT_STATUSES : EXTERNAL_PROJECT_STATUSES
    },
    icon: 'IconFileCheck2'
  },
  children: [
    {
      name: 'filters',
      enabled: true,
      title: {
        en: 'Filters',
        sv: 'Filter'
      },
      component: 'ProjectAnalysisFilters'
    },
    {
      name: 'data',
      enabled: true,
      title: {
        en: 'Data',
        sv: 'Data'
      },
      children: [
        {
          name: 'overview',
          enabled: true,
          title: {
            en: 'Overview',
            sv: 'Översikt'
          },
          isRoute: true,
          component: 'DashboardDataSectionFlat',
          props: {
            dataSources: PROJECT_STATUSES_MEASUREMENTS.flatMap((status, i) => [
              {
                id: `project.measurement.${status.toLocaleLowerCase()}.value`,
                type: 'fetch',
                fetch: 'fetch.project.measurements',
                path: `measurements[${i * 2}].splits[0].value`
              },
              {
                id: `project.measurement.${status.toLocaleLowerCase()}.colored-entities`,
                type: 'fetch',
                fetch: 'fetch.project.measurements',
                transform: {
                  id: 'portfolio.apartments.colored-entities',
                  index: i * 2 + 1
                }
              },
              {
                id: `project.measurement.${status.toLocaleLowerCase()}.details`,
                type: 'fetch',
                fetch: 'fetch.project.measurements.detail',
                transform: {
                  id: 'projects.detail',
                  status,
                  detailRowNames
                }
              }
            ])
          },
          children: PROJECT_STATUSES_MEASUREMENTS.map((status, i) => ({
            name: status.toLocaleLowerCase().replace('_', '-'),
            title: titles[status],
            enabled: true,
            component: 'MainMeasurementCard',
            props: {
              initialShowOnMap: i === 0,
              value: {
                dataSource: `project.measurement.${status.toLocaleLowerCase()}.value`
              },
              coloredEntities: {
                dataSource: `project.measurement.${status.toLocaleLowerCase()}.colored-entities`
              },
              table: {
                dataSource: `project.measurement.${status.toLocaleLowerCase()}.details`,
                headers: detailHeaders
              },
              colorLegend: {
                hideMean: true
              },
              color: colors[status],
              colorScale: toColorScale(colors[status])
            }
          }))
        },
        {
          name: 'projectlist',
          enabled: true,
          title: {
            en: 'Project list',
            sv: 'Projektlista'
          },
          isRoute: true,
          component: 'DashboardDataSectionFlat',
          info: {
            en: 'Info about project list.',
            sv: 'Info om projektlista'
          },
          props: {
            dataSources: []
          },
          children: [
            {
              name: 'Project list',
              enabled: true,
              title: {
                en: 'Project list',
                sv: 'Projektlista'
              },
              component: 'ProjectListTable',
              props: {},
              children: []
            }
          ]
        }
      ]
    }
  ]
})

export const assets: Feature = {
  name: 'assets',
  enabled: true,
  access: ['FEAT_PROJECT-ANALYSIS-INTERNAL_READER', 'FEAT_PROJECT-ANALYSIS-EXTERNAL_READER'],
  title: {
    en: 'Asset management',
    sv: 'Förvaltning'
  },
  component: 'FeatureCard',
  isRoute: true,
  props: {
    icon: 'IconTool2',
    disableIsDrawPointActive: true,
    subtitle: {
      en: 'Subfeatures',
      sv: 'Delmoduler'
    }
  },
  children: [
    {
      name: 'todo',
      enabled: true,
      title: {
        en: 'Operation & maintenance',
        sv: 'Drift & Underhåll'
      },

      props: {
        icon: 'IconTimeLine',
        disableIsDrawPointActive: true
      },
      children: [ticketAnalysis]
    },
    {
      name: 'projects',
      enabled: true,
      title: {
        en: 'Projects',
        sv: 'Projekt'
      },

      props: {
        icon: 'IconTimeLine',
        disableIsDrawPointActive: true
      },
      children: [
        createProjectAnalysisChild({ internal: true }),
        createProjectAnalysisChild({ internal: false })
      ]
    }
  ]
}
